/**
 * PMCrossPersonaWidgets — Renders PM widgets on the Executive dashboard.
 *
 * When an Executive persona user adds PM widgets via the settings cog,
 * the widget IDs are stored in the executive's preferences. The Executive
 * shell delegates rendering of those PM-specific widget IDs to this module.
 *
 * Architecture:
 *   - PMCrossPersonaDataProvider: context that fetches PM-scoped data once
 *   - PMCrossPersonaWidget: renders a single PM widget using context data
 *
 * Data is scoped to the current user's PM projects (same as the PM dashboard).
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  TrendingDown,
  TrendingUp,
  Banknote,
  FileX,
  BarChart3,
  Diamond,
  DollarSign,
  CalendarDays,
  ShieldAlert,
  Layers,
  Users,
  ClipboardList,
  FileText,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatHours } from '@/lib/budget-utils';
import { KpiCard } from '@/components/kpi-card';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import { useDashboardData } from '@/contexts/DashboardDataContext';
import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import {
  fetchPMDashboardProjectsSummary,
  fetchDashboardKpiSnapshot,
  fetchDashboardScheduleHealth,
  fetchDashboardBillingEvents,
  fetchTimeEntriesByProjects,
  fetchEmployees,
  fetchResourceAllocations,
  getDataMode,
} from '@/lib/data-service';
import type {
  DashboardKpiSnapshot,
  TimeEntryWithEmployee,
  ScheduleHealthResponse,
  DashboardBillingEvent,
  PMProjectSummaryRow,
} from '@/lib/data-service';
import type { Project, Employee, ResourceAllocation } from '@/types';
import type { BillingMilestone } from '@/types/budget.types';
import { getBudgetDataByProjectId } from '@/lib/mock-data/budget-mock-data';
import {
  mockEmployees,
} from '@/lib/mock-data';

// ── Context shape ─────────────────────────────────────────────────────────────

interface PMCrossPersonaData {
  activeProjects: Project[];
  projectsLoading: boolean;
  employees: Employee[];
  allocations: ResourceAllocation[];
  billingEvents: DashboardBillingEvent[];
  timeEntries: TimeEntryWithEmployee[];
  scheduleHealthData: ScheduleHealthResponse | null;
  schedulingLoading: boolean;
  pmPriorSnapshot: DashboardKpiSnapshot | null;
  liveDataLoading: boolean;
  isLiveMode: boolean;
  currentPmId: number | null;
}

const PMCrossPersonaDataContext = createContext<PMCrossPersonaData | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function PMCrossPersonaDataProvider({ children }: { children: ReactNode }) {
  const { authenticatedUser } = useAuth();
  const { currentCompany } = useCompany();
  const { activeProjects: ctxActiveProjects, projectsLoading: ctxProjectsLoading } = useDashboardData();

  const dataMode = getDataMode();
  const isLiveMode = dataMode === 'live';
  const pmId = authenticatedUser?.id ? parseInt(authenticatedUser.id, 10) : undefined;
  const currentPmId = pmId && !isNaN(pmId) ? pmId : null;

  // PM-scoped projects
  const [pmProjects, setPmProjects] = useState<Project[]>([]);
  const [pmProjectsLoading, setPmProjectsLoading] = useState(isLiveMode);

  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    setPmProjectsLoading(true);
    fetchPMDashboardProjectsSummary(pmId)
      .then(resp => {
        if (cancelled) return;
        const mapped: Project[] = resp.projects.map((r: PMProjectSummaryRow) => ({
          id: r.id,
          name: r.name,
          code: r.code,
          clientNames: r.clientNames,
          health: r.health,
          statusId: r.statusId,
          completionPercent: r.completionPercent,
          completionPercentOverride: r.completionPercentOverride,
          budgetRevenue: r.budgetRevenue,
          actualRevenue: r.actualRevenue,
          actualLabour: r.actualLabour,
          actualExpense: r.actualExpense,
          budgetLabour: r.budgetLabour,
          budgetExpense: r.budgetExpense,
          budgetCost: r.budgetCost,
          actualCosts: r.actualCosts,
          budgetHours: r.budgetHours,
          actualHours: r.actualHours,
          hoursVariance: r.hoursVariance,
          costVariancePercent: r.costVariancePercent,
          wipAmount: r.wipAmount,
          endDate: r.endDate,
          forecastEndDate: r.forecastEndDate,
          startDate: r.startDate,
          projectManagerId: r.projectManagerId,
          type: r.type,
        } as unknown as Project));
        setPmProjects(mapped);
      })
      .catch(() => { if (!cancelled) setPmProjects([]); })
      .finally(() => { if (!cancelled) setPmProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [isLiveMode, pmId, currentCompany?.id]);

  const activeProjects = isLiveMode ? pmProjects : ctxActiveProjects;
  const projectsLoading = isLiveMode ? pmProjectsLoading : ctxProjectsLoading;

  // Employees + allocations
  const [liveEmployees, setLiveEmployees] = useState<Employee[]>([]);
  const [liveAllocations, setLiveAllocations] = useState<ResourceAllocation[]>([]);
  const [liveDataLoading, setLiveDataLoading] = useState(false);

  useEffect(() => {
    if (!isLiveMode || projectsLoading) return;
    let cancelled = false;
    setLiveDataLoading(true);
    Promise.allSettled([
      fetchEmployees('dashboard'),
      fetchResourceAllocations('dashboard'),
    ]).then(([empResult, allocResult]) => {
      if (cancelled) return;
      if (empResult.status === 'fulfilled') setLiveEmployees(empResult.value as Employee[]);
      if (allocResult.status === 'fulfilled') setLiveAllocations(allocResult.value as ResourceAllocation[]);
      setLiveDataLoading(false);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, projectsLoading, currentCompany?.id]);

  // Billing events
  const [liveBillingEvents, setLiveBillingEvents] = useState<DashboardBillingEvent[]>([]);
  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    fetchDashboardBillingEvents(pmId).then(events => {
      if (!cancelled) setLiveBillingEvents(events);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, pmId, currentCompany?.id]);

  // KPI snapshot
  const [pmPriorSnapshot, setPmPriorSnapshot] = useState<DashboardKpiSnapshot | null>(null);
  useEffect(() => {
    // The endpoint requires the X-Company-Id header, which authFetch only sends
    // once company context has resolved.
    if (!isLiveMode || !currentCompany?.id) return;
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    fetchDashboardKpiSnapshot('project_manager', 'weekly', today).then(snap => {
      if (!cancelled) setPmPriorSnapshot(snap);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, currentCompany?.id]);

  // Time entries
  const [liveTimeEntries, setLiveTimeEntries] = useState<TimeEntryWithEmployee[]>([]);
  useEffect(() => {
    if (!isLiveMode || projectsLoading || activeProjects.length === 0) return;
    let cancelled = false;
    const projIds = activeProjects.map(p => p.id);
    const now = new Date();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    fetchTimeEntriesByProjects(projIds, fmt(monday), fmt(sunday)).then(entries => {
      if (!cancelled) setLiveTimeEntries(entries);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, projectsLoading, activeProjects, currentCompany?.id]);

  // Schedule health
  const [scheduleHealthData, setScheduleHealthData] = useState<ScheduleHealthResponse | null>(null);
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    setSchedulingLoading(true);
    fetchDashboardScheduleHealth(pmId).then(data => {
      if (!cancelled) {
        setScheduleHealthData(data);
        setSchedulingLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isLiveMode, pmId, currentCompany?.id]);

  const value = useMemo<PMCrossPersonaData>(() => ({
    activeProjects,
    projectsLoading,
    employees: isLiveMode ? liveEmployees : mockEmployees,
    allocations: isLiveMode ? liveAllocations : [],
    billingEvents: liveBillingEvents,
    timeEntries: liveTimeEntries,
    scheduleHealthData,
    schedulingLoading,
    pmPriorSnapshot,
    liveDataLoading,
    isLiveMode,
    currentPmId,
  }), [
    activeProjects, projectsLoading, isLiveMode, liveEmployees, liveAllocations,
    liveBillingEvents, liveTimeEntries, scheduleHealthData, schedulingLoading,
    pmPriorSnapshot, liveDataLoading, currentPmId,
  ]);

  return (
    <PMCrossPersonaDataContext.Provider value={value}>
      {children}
    </PMCrossPersonaDataContext.Provider>
  );
}

// ── Widget renderer ───────────────────────────────────────────────────────────

interface PMCrossPersonaWidgetProps {
  widgetId: string;
}

export function PMCrossPersonaWidget({ widgetId }: PMCrossPersonaWidgetProps) {
  const data = useContext(PMCrossPersonaDataContext);
  const navigateHook = useNavigate();
  const { plannerStatus } = useDashboardIntegrationStatus();
  const isPlannerConnected = plannerStatus.isConnected;

  // Widget-level state (hoisted for stable hook ordering)
  const [burnTab, setBurnTab] = useState<'cost' | 'hours'>('cost');

  if (!data) return null;

  const {
    activeProjects,
    projectsLoading,
    employees,
    allocations,
    billingEvents,
    timeEntries,
    scheduleHealthData,
    schedulingLoading,
    pmPriorSnapshot,
    liveDataLoading,
    isLiveMode,
    currentPmId,
  } = data;

  const handleNavigate = (page: string) => navigateHook(`/${page}`);

  // PM-scoped derived data
  const pmActiveProjects = (() => {
    if (!currentPmId) return activeProjects;
    const scoped = activeProjects.filter(p => p.projectManagerId === currentPmId);
    return scoped.length > 0 ? scoped : activeProjects;
  })();

  // Loading gate
  if (projectsLoading || (isLiveMode && liveDataLoading)) {
    return (
      <Card key={widgetId}>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  // ── KPI helpers ──────────────────────────────────────────────────────────────
  const myActiveCount = pmActiveProjects.length;
  const myHoursRemaining = pmActiveProjects.reduce((sum, p) => sum + (p.hoursVariance ?? 0), 0);
  const myOnTrackCount = pmActiveProjects.filter(p => p.health === 'green').length;
  const myOverdueMilestoneCount = (() => {
    if (isLiveMode) return billingEvents.filter(e => e.eventType === 'overdue').length;
    let count = 0;
    for (const project of pmActiveProjects) {
      try {
        const budgetData = getBudgetDataByProjectId(project.id);
        const milestones = budgetData?.salesBudget?.milestones ?? [];
        count += milestones.filter((m: BillingMilestone) => m.status === 'overdue').length;
      } catch { /* no data */ }
    }
    return count;
  })();

  // Weekly deltas
  const pmIdSeed = pmActiveProjects.reduce((sum, p) => sum + p.id, 0);
  const priorActiveCount = isLiveMode ? (pmPriorSnapshot?.pmActiveProjectCount ?? myActiveCount) : Math.max(0, myActiveCount + (pmIdSeed % 3) - 1);
  const priorOnTrackCount = isLiveMode ? (pmPriorSnapshot?.pmOnTrackCount ?? myOnTrackCount) : Math.max(0, myOnTrackCount + ((pmIdSeed % 5) - 2));
  const priorOverdueCount = isLiveMode ? (pmPriorSnapshot?.pmOverdueMilestoneCount ?? myOverdueMilestoneCount) : Math.max(0, myOverdueMilestoneCount - (pmIdSeed % 2));
  const activeCountDelta = myActiveCount - priorActiveCount;
  const onTrackDelta = myOnTrackCount - priorOnTrackCount;
  const overdueDelta = myOverdueMilestoneCount - priorOverdueCount;

  switch (widgetId) {
    // ── My Project Pulse (KPI tiles) ─────────────────────────────────────────
    case 'pm_pulse': {
      const hoursIsNegative = myHoursRemaining < 0;
      const allOnTrack = myOnTrackCount === myActiveCount && myActiveCount > 0;
      const hasOverdue = myOverdueMilestoneCount > 0;

      return (
        <div key="pm_pulse" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            title="My Active Projects"
            value={myActiveCount}
            icon={FolderKanban}
            accentVariant="info"
            trend={activeCountDelta !== 0 ? { value: activeCountDelta, isPositive: activeCountDelta >= 0 } : undefined}
            description="vs last week"
          />
          <KpiCard
            title="Hours Remaining"
            value={hoursIsNegative ? `−${formatHours(Math.abs(myHoursRemaining))} hrs` : `${formatHours(myHoursRemaining)} hrs`}
            icon={Clock}
            accentVariant={hoursIsNegative ? 'error' : 'success'}
            description={hoursIsNegative ? 'Hours overrun across projects' : 'Remaining budget across projects'}
          />
          <KpiCard
            title="Projects On Track"
            value={`${myOnTrackCount} / ${myActiveCount}`}
            icon={allOnTrack ? CheckCircle2 : AlertTriangle}
            accentVariant={allOnTrack ? 'success' : myOnTrackCount < myActiveCount / 2 ? 'error' : 'warning'}
            trend={onTrackDelta !== 0 ? { value: onTrackDelta, isPositive: onTrackDelta >= 0 } : undefined}
            description={allOnTrack ? 'All projects healthy' : `${myActiveCount - myOnTrackCount} need attention`}
          />
          <KpiCard
            title="Pending Actions"
            value={myOverdueMilestoneCount}
            icon={hasOverdue ? AlertCircle : CheckCircle2}
            accentVariant={hasOverdue ? (myOverdueMilestoneCount >= 3 ? 'error' : 'warning') : 'success'}
            trend={overdueDelta !== 0 ? { value: overdueDelta, isPositive: overdueDelta <= 0 } : undefined}
            description={hasOverdue ? `Overdue billing milestone${myOverdueMilestoneCount !== 1 ? 's' : ''}` : 'No overdue billing milestones'}
          />
        </div>
      );
    }

    // ── My Projects Health ────────────────────────────────────────────────────
    case 'my_projects_health': {
      const redProjects = pmActiveProjects.filter(p => p.health === 'red');
      const amberProjects = pmActiveProjects.filter(p => p.health === 'amber');
      const greenCount = pmActiveProjects.filter(p => p.health === 'green').length;
      const total = pmActiveProjects.length;
      const attentionProjects = [...redProjects, ...amberProjects].slice(0, 4);
      const hrsOverrunCount = pmActiveProjects.filter(p => (p.hoursVariance ?? 0) < 0).length;
      const allGreen = redProjects.length === 0 && amberProjects.length === 0;

      function getPrimarySignal(project: typeof pmActiveProjects[0]): { label: string; colour: string } {
        if ((project.costVariancePercent ?? 0) < -10) return { label: 'Over Budget', colour: 'destructive' };
        if (project.forecastEndDate && project.endDate && project.forecastEndDate > project.endDate) return { label: 'Running Over', colour: 'warning' };
        if (project.budgetHours > 0 && (project.hoursVariance ?? 0) < project.budgetHours * 0.1 && (project.hoursVariance ?? 0) >= 0) return { label: 'Hours Critical', colour: 'warning' };
        return { label: project.health === 'red' ? 'Off Track' : 'At Risk', colour: project.health === 'red' ? 'destructive' : 'warning' };
      }

      if (total === 0) {
        return (
          <Card key="my_projects_health">
            <CardHeader>
              <CardTitle className="text-base">My Projects Health</CardTitle>
              <CardDescription>Overall health across your active projects</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                <FileX className="h-10 w-10 opacity-30" />
                <p className="text-sm font-medium">No active projects</p>
                <p className="text-xs">Projects you manage will appear here once they are active.</p>
              </div>
            </CardContent>
          </Card>
        );
      }

      return (
        <Card key="my_projects_health">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">My Projects Health</CardTitle>
                <CardDescription className="mt-0.5">
                  Overall health across {total} active project{total !== 1 ? 's' : ''}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleNavigate('projects')} className="gap-1 text-xs text-muted-foreground">
                View all projects <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* RAG bar */}
            <div className="space-y-2">
              <div className="flex h-3 w-full rounded-full overflow-hidden gap-px">
                {redProjects.length > 0 && <div className="bg-destructive h-full transition-all" style={{ width: `${(redProjects.length / total) * 100}%` }} />}
                {amberProjects.length > 0 && <div className="bg-warning-500 h-full transition-all" style={{ width: `${(amberProjects.length / total) * 100}%` }} />}
                {greenCount > 0 && <div className="bg-success-500 h-full transition-all" style={{ width: `${(greenCount / total) * 100}%` }} />}
              </div>
              <div className="flex items-center gap-5 text-xs text-muted-foreground">
                {redProjects.length > 0 && (
                  <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-destructive shrink-0" /><span className="font-medium text-destructive">{redProjects.length}</span> Off Track</span>
                )}
                {amberProjects.length > 0 && (
                  <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-warning-500 shrink-0" /><span className="font-medium text-warning-600">{amberProjects.length}</span> At Risk</span>
                )}
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-success-500 shrink-0" /><span className="font-medium text-success-600">{greenCount}</span> On Track</span>
                {hrsOverrunCount > 0 && (
                  <span className="flex items-center gap-1.5 ml-auto"><TrendingDown className="h-3 w-3 text-destructive shrink-0" />{hrsOverrunCount} hours overrun</span>
                )}
              </div>
            </div>

            {allGreen && (
              <div className="flex items-center gap-3 rounded-lg bg-success-50 border border-success-200 px-4 py-3">
                <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-success-700">All projects on track</p>
                  <p className="text-xs text-success-600 mt-0.5">No issues detected across your {total} active project{total !== 1 ? 's' : ''}.</p>
                </div>
              </div>
            )}

            {!allGreen && attentionProjects.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Needs Attention</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {attentionProjects.map(project => {
                    const signal = getPrimarySignal(project);
                    const isRed = project.health === 'red';
                    const signalColour: Record<string, string> = {
                      destructive: 'bg-destructive/10 text-destructive border-destructive/20',
                      warning: 'bg-warning-100 text-warning-700 border-warning-200',
                    };
                    return (
                      <button key={project.id} onClick={() => handleNavigate(`projects/${project.id}`)} className={cn('flex items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 w-full', isRed ? 'border-destructive/20 bg-destructive/5' : 'border-warning-200 bg-warning-50/50')}>
                        <span className={cn('mt-0.5 h-2.5 w-2.5 rounded-full shrink-0', isRed ? 'bg-destructive' : 'bg-warning-500')} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium leading-snug truncate">{project.name}</p>
                          {project.clientNames && <p className="text-xs text-muted-foreground mt-0.5 truncate">{project.clientNames}</p>}
                        </div>
                        <Badge variant="outline" className={cn('text-xs font-medium whitespace-nowrap shrink-0', signalColour[signal.colour] ?? 'bg-muted text-muted-foreground')}>{signal.label}</Badge>
                      </button>
                    );
                  })}
                </div>
                {(redProjects.length + amberProjects.length) > 4 && (
                  <button onClick={() => handleNavigate('projects')} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 pt-0.5">
                    +{(redProjects.length + amberProjects.length) - 4} more need attention <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // ── Budget & Hours Burn ───────────────────────────────────────────────────
    case 'budget_hours_burn': {
      const burnRows = pmActiveProjects
        .map(p => {
          const costBudget = p.budgetCost ?? ((p.budgetLabour ?? 0) + (p.budgetExpense ?? 0));
          const actualCost = p.actualCosts ?? 0;
          const costBurnPct = costBudget > 0 ? (actualCost / costBudget) * 100 : 0;
          const hoursBudget = p.budgetHours ?? 0;
          const actualHours = p.actualHours ?? 0;
          const hoursBurnPct = hoursBudget > 0 ? (actualHours / hoursBudget) * 100 : 0;
          const burnPct = burnTab === 'cost' ? costBurnPct : hoursBurnPct;
          const isOver = burnPct > 100;
          const completionPct = p.completionPercent ?? 0;
          return { id: p.id, name: p.name, clientNames: p.clientNames ?? '', costBudget, actualCost, costBurnPct, hoursBudget, actualHours, hoursBurnPct, burnPct, overPct: Math.max(0, burnPct - 100), isOver, completionPct };
        })
        .sort((a, b) => { if (a.isOver !== b.isOver) return a.isOver ? -1 : 1; return b.burnPct - a.burnPct; });

      const getBurnColour = (pct: number) => pct > 100 ? 'bg-destructive' : pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-green-500';
      const getBurnTextColour = (pct: number) => pct > 100 ? 'text-destructive font-bold' : pct > 90 ? 'text-red-600 font-semibold' : pct > 70 ? 'text-amber-600' : 'text-green-600';

      if (pmActiveProjects.length === 0) {
        return (
          <Card key="budget_hours_burn">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" />Budget &amp; Hours Burn</CardTitle></CardHeader>
            <CardContent><div className="flex flex-col items-center justify-center py-10 text-center gap-2"><BarChart3 className="h-10 w-10 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No active projects to display</p></div></CardContent>
          </Card>
        );
      }

      return (
        <Card key="budget_hours_burn">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" />Budget &amp; Hours Burn</CardTitle>
                <CardDescription className="mt-0.5">Sorted by burn % — highest risk first</CardDescription>
              </div>
              <div className="flex items-center rounded-md border bg-muted/30 p-0.5 gap-0.5 shrink-0">
                <button onClick={() => setBurnTab('cost')} className={cn('flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors', burnTab === 'cost' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                  <DollarSign className="h-3 w-3" />Cost Burn
                </button>
                <button onClick={() => setBurnTab('hours')} className={cn('flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors', burnTab === 'hours' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                  <Clock className="h-3 w-3" />Hours Burn
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-3">
              {burnRows.slice(0, 5).map(row => {
                const fillPct = Math.min(row.burnPct, 100);
                const emptyPct = Math.max(0, 100 - fillPct);
                const diamondLeft = Math.min(row.completionPct, 100);
                return (
                  <div key={row.id} className="group cursor-pointer rounded-md p-2 hover:bg-muted/40 transition-colors" onClick={() => handleNavigate(`projects/${row.id}?tab=cost-budget`)}>
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                      <div className="min-w-0">
                        <span className="text-xs font-semibold truncate block">{row.name}</span>
                        {row.clientNames && <span className="text-[10px] text-muted-foreground truncate block">{row.clientNames}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {row.isOver && <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">OVER</Badge>}
                        <span className={cn('text-xs tabular-nums', getBurnTextColour(row.burnPct))}>{row.burnPct.toFixed(0)}%</span>
                        {burnTab === 'cost' ? (
                          <span className="text-[10px] text-muted-foreground tabular-nums">£{(row.actualCost / 1000).toFixed(0)}k / £{(row.costBudget / 1000).toFixed(0)}k</span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground tabular-nums">{formatHours(row.actualHours)}h / {formatHours(row.hoursBudget)}h</span>
                        )}
                      </div>
                    </div>
                    <div className="relative">
                      <div className="flex h-4 w-full rounded overflow-hidden bg-muted">
                        <div className={cn('h-full transition-all', getBurnColour(row.burnPct))} style={{ width: `${fillPct}%` }} />
                        <div className="h-full bg-muted-foreground/10" style={{ width: `${emptyPct}%` }} />
                      </div>
                      {row.isOver && <div className="absolute top-0 right-0 h-full bg-destructive/30 rounded-r" style={{ width: `${Math.min(row.overPct, 30)}%` }} />}
                      {row.completionPct > 0 && (
                        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10" style={{ left: `${diamondLeft}%` }}>
                          <Diamond className={cn('h-3 w-3 fill-current drop-shadow', row.burnPct > row.completionPct ? 'text-red-600' : 'text-green-600')} />
                        </div>
                      )}
                    </div>
                    {row.completionPct > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        {row.burnPct > row.completionPct + 10 ? (
                          <><TrendingUp className="h-2.5 w-2.5 text-red-500" /><span className="text-[10px] text-red-600">Burn ahead of completion by {(row.burnPct - row.completionPct).toFixed(0)}%</span></>
                        ) : row.burnPct < row.completionPct - 10 ? (
                          <><TrendingDown className="h-2.5 w-2.5 text-green-500" /><span className="text-[10px] text-green-600">Completion ahead of burn</span></>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">Burn tracking with completion</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {burnRows.length > 5 && (
              <button onClick={() => handleNavigate('projects')} className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full justify-center py-1">
                +{burnRows.length - 5} more projects — View all <ChevronRight className="h-3 w-3" />
              </button>
            )}
            <div className="mt-4 pt-3 border-t flex items-center gap-4 text-[10px] text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-green-500" /><span>&lt;70% burn</span></div>
              <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-amber-500" /><span>70–90%</span></div>
              <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-red-500" /><span>90–100%</span></div>
              <div className="flex items-center gap-1"><div className="h-2.5 w-2.5 rounded-sm bg-destructive" /><span>Over budget</span></div>
              <div className="flex items-center gap-1"><Diamond className="h-2.5 w-2.5 fill-current text-muted-foreground" /><span>◆ Completion %</span></div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // ── Schedule Health ───────────────────────────────────────────────────────
    case 'schedule_health': {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isPlannerConnected || isLiveMode) {
        const total = scheduleHealthData?.totalProjects ?? pmActiveProjects.length;
        const criticalCount = scheduleHealthData?.criticalCount ?? 0;
        const warningCount = scheduleHealthData?.warningCount ?? 0;
        const healthyCount = scheduleHealthData?.healthyCount ?? total;
        const unmetRolesTotal = scheduleHealthData?.unmetRolesTotal ?? 0;
        const attentionRows = scheduleHealthData?.attentionProjects ?? [];
        const shownAttention = attentionRows.slice(0, 4);
        const extraAttention = attentionRows.length - shownAttention.length;

        return (
          <Card key="schedule_health">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /><CardTitle className="text-base">Schedule Health</CardTitle></div>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground" onClick={() => handleNavigate('scheduling')}>View all <ChevronRight className="h-3 w-3" /></Button>
              </div>
              <CardDescription className="text-xs">Scheduling coverage and warnings across your active projects</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              {schedulingLoading ? (
                <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : total === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                  <FileX className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm font-medium text-muted-foreground">No active projects</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 rounded-lg border bg-muted/30 px-4 py-3 mb-4">
                    <div className="flex flex-col items-center min-w-[36px]">
                      <span className="text-lg font-bold leading-none">{total}</span>
                      <span className="text-[10px] text-muted-foreground mt-0.5">Projects</span>
                    </div>
                    <div className="w-px h-8 bg-border" />
                    <div className="flex items-center gap-3 flex-1 flex-wrap">
                      {criticalCount > 0 && <div className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-red-500 shrink-0" /><span className="text-xs font-medium text-red-600">{criticalCount} critical</span></div>}
                      {warningCount > 0 && <div className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-amber-500 shrink-0" /><span className="text-xs font-medium text-amber-600">{warningCount} at risk</span></div>}
                      {healthyCount > 0 && <div className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-green-500 shrink-0" /><span className="text-xs text-muted-foreground">{healthyCount} healthy</span></div>}
                    </div>
                    {unmetRolesTotal > 0 && <div className="flex items-center gap-1 text-xs text-red-600 shrink-0"><ShieldAlert className="h-3 w-3" /><span>{unmetRolesTotal} unmet role{unmetRolesTotal !== 1 ? 's' : ''}</span></div>}
                  </div>
                  {attentionRows.length === 0 ? (
                    <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                      <p className="text-xs text-green-700 font-medium">All projects have healthy scheduling coverage</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Needs attention</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {shownAttention.map(proj => {
                          const isCritical = proj.healthStatus === 'critical';
                          const signal = proj.coveragePercent < 100 ? `${proj.coveragePercent}% coverage` : proj.unmetRoles > 0 ? `${proj.unmetRoles} unmet role${proj.unmetRoles !== 1 ? 's' : ''}` : 'Review needed';
                          return (
                            <button key={proj.projectId} className={cn('text-left rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/50', isCritical ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50')} onClick={() => handleNavigate(`projects/${proj.projectId}?tab=resourcing`)}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0"><p className="text-sm font-medium leading-tight truncate">{proj.projectName}</p><p className="text-xs text-muted-foreground truncate">{proj.clientNames ?? 'No client'}</p></div>
                                <span className={cn('text-xs font-semibold shrink-0', proj.coveragePercent < 50 ? 'text-red-600' : proj.coveragePercent < 95 ? 'text-amber-600' : 'text-green-600')}>{proj.coveragePercent}%</span>
                              </div>
                              <p className={cn('text-xs mt-1 truncate', isCritical ? 'text-red-600' : 'text-amber-600')}>{signal}</p>
                            </button>
                          );
                        })}
                      </div>
                      {extraAttention > 0 && <button className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1" onClick={() => handleNavigate('scheduling')}>+{extraAttention} more need attention →</button>}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      }

      // Basic mode: dates-only fallback
      const basicRows = pmActiveProjects.map(p => {
        const end = p.endDate ? new Date(p.endDate) : null;
        const forecast = p.forecastEndDate ? new Date(p.forecastEndDate) : null;
        const daysRemaining = end ? Math.ceil((end.getTime() - today.getTime()) / 86_400_000) : null;
        const isOverrun = forecast && end ? forecast > end : false;
        return { p, end, forecast, daysRemaining, isOverrun };
      });
      const total = basicRows.length;
      const overrunCount = basicRows.filter(r => r.isOverrun).length;

      return (
        <Card key="schedule_health">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /><CardTitle className="text-base">Schedule Health</CardTitle></div>
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">Dates Only</Badge>
            </div>
            <CardDescription className="text-xs">Contracted vs forecast end dates across your active projects</CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex items-center gap-4 rounded-lg border bg-muted/30 px-4 py-3 mb-4">
              <div className="flex flex-col items-center min-w-[36px]">
                <span className="text-lg font-bold leading-none">{total}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Projects</span>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-3 flex-1">
                {overrunCount > 0 && <div className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-red-500 shrink-0" /><span className="text-xs font-medium text-red-600">{overrunCount} forecast overrun</span></div>}
                <div className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-green-500 shrink-0" /><span className="text-xs text-muted-foreground">{total - overrunCount} on schedule</span></div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // ── My Team This Week ─────────────────────────────────────────────────────
    case 'my_team_this_week': {
      // Aggregate team members from allocations across PM's projects
      const projectIds = new Set(pmActiveProjects.map(p => p.id));
      const teamAllocations = allocations.filter(a => projectIds.has(a.projectId));
      const teamMemberIds = [...new Set(teamAllocations.map(a => a.employeeId))];
      const teamMembers = teamMemberIds.map(id => employees.find(e => e.id === id)).filter(Boolean) as Employee[];

      if (teamMembers.length === 0) {
        return (
          <Card key="my_team_this_week">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" />My Team This Week</CardTitle>
              <CardDescription>Cross-project team booking status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                <Users className="h-10 w-10 opacity-30" />
                <p className="text-sm font-medium">No team allocations found</p>
                <p className="text-xs">Team members will appear here once they are allocated to your projects.</p>
              </div>
            </CardContent>
          </Card>
        );
      }

      const bookedCount = teamMembers.length;
      return (
        <Card key="my_team_this_week">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" />My Team This Week</CardTitle>
                <CardDescription className="mt-0.5">{bookedCount} team member{bookedCount !== 1 ? 's' : ''} allocated across your projects</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {teamMembers.slice(0, 6).map(emp => {
                const empAllocations = teamAllocations.filter(a => a.employeeId === emp.id);
                const totalHours = empAllocations.reduce((sum, a) => sum + (a.allocatedHours ?? 0), 0);
                return (
                  <div key={emp.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{emp.name}</p>
                      <p className="text-xs text-muted-foreground">{empAllocations.length} project{empAllocations.length !== 1 ? 's' : ''}</p>
                    </div>
                    <span className="text-xs font-medium tabular-nums text-muted-foreground shrink-0">{formatHours(totalHours)}h</span>
                  </div>
                );
              })}
              {teamMembers.length > 6 && (
                <p className="text-xs text-muted-foreground text-center pt-1">+{teamMembers.length - 6} more team members</p>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    // ── Upcoming Billing Events ───────────────────────────────────────────────
    case 'upcoming_billing_events': {
      const upcoming = billingEvents
        .filter(e => e.eventType !== 'overdue')
        .sort((a, b) => new Date(a.dueDate ?? '').getTime() - new Date(b.dueDate ?? '').getTime())
        .slice(0, 5);
      const overdue = billingEvents.filter(e => e.eventType === 'overdue');

      return (
        <Card key="upcoming_billing_events">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Banknote className="h-4 w-4 text-muted-foreground" />Upcoming Billing Events</CardTitle>
                <CardDescription className="mt-0.5">Milestones and billing triggers in next 30 days</CardDescription>
              </div>
              {overdue.length > 0 && (
                <Badge variant="destructive" className="text-xs">{overdue.length} overdue</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {billingEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                <Banknote className="h-10 w-10 opacity-30" />
                <p className="text-sm font-medium">No upcoming billing events</p>
              </div>
            ) : (
              <div className="space-y-2">
                {overdue.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    <p className="text-xs font-medium text-destructive uppercase tracking-wide">Overdue</p>
                    {overdue.slice(0, 3).map((evt, i) => (
                      <div key={`overdue-${i}`} className="flex items-center justify-between rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{evt.projectName}</p>
                          <p className="text-xs text-muted-foreground truncate">{evt.milestoneName ?? 'Billing event'}</p>
                        </div>
                        <span className="text-xs text-destructive font-medium shrink-0">{evt.dueDate ? new Date(evt.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
                {upcoming.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Upcoming</p>
                    {upcoming.map((evt, i) => (
                      <div key={`upcoming-${i}`} className="flex items-center justify-between rounded-md border px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{evt.projectName}</p>
                          <p className="text-xs text-muted-foreground truncate">{evt.milestoneName ?? 'Billing event'}</p>
                        </div>
                        <span className="text-xs text-muted-foreground font-medium shrink-0">{evt.dueDate ? new Date(evt.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // ── Timesheet Status ─────────────────────────────────────────────────────
    case 'timesheet_status': {
      // Aggregate time entries by employee for this week
      const projectIds = new Set(pmActiveProjects.map(p => p.id));
      const weekEntries = timeEntries.filter(e => projectIds.has(e.projectId));
      const byEmployee = new Map<number, { name: string; totalHours: number; daysLogged: Set<string> }>();

      for (const entry of weekEntries) {
        const existing = byEmployee.get(entry.employeeId);
        if (existing) {
          existing.totalHours += entry.hours ?? 0;
          if (entry.date) existing.daysLogged.add(entry.date);
        } else {
          byEmployee.set(entry.employeeId, {
            name: entry.employeeName ?? `Employee ${entry.employeeId}`,
            totalHours: entry.hours ?? 0,
            daysLogged: new Set(entry.date ? [entry.date] : []),
          });
        }
      }

      const rows = [...byEmployee.entries()]
        .map(([id, data]) => ({ id, ...data, daysCount: data.daysLogged.size }))
        .sort((a, b) => b.totalHours - a.totalHours);

      const totalTeamHours = rows.reduce((sum, r) => sum + r.totalHours, 0);

      return (
        <Card key="timesheet_status">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-4 w-4 text-muted-foreground" />Timesheet Status</CardTitle>
                <CardDescription className="mt-0.5">Team timesheet submission for the current week</CardDescription>
              </div>
              {rows.length > 0 && (
                <Badge variant="outline" className="text-xs font-normal">{formatHours(totalTeamHours)}h total</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                <ClipboardList className="h-10 w-10 opacity-30" />
                <p className="text-sm font-medium">No timesheet entries this week</p>
                <p className="text-xs">Team entries will appear here as they log time.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {rows.slice(0, 8).map(row => (
                  <div key={row.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{row.name}</p>
                      <p className="text-xs text-muted-foreground">{row.daysCount} day{row.daysCount !== 1 ? 's' : ''} logged</p>
                    </div>
                    <span className="text-xs font-medium tabular-nums shrink-0">{formatHours(row.totalHours)}h</span>
                  </div>
                ))}
                {rows.length > 8 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">+{rows.length - 8} more team members</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // ── Quick Links PM ────────────────────────────────────────────────────────
    case 'quick_links_pm': {
      const pmParam = currentPmId ? `?managerId=${currentPmId}` : '';
      const pmQuickLinks = [
        { id: 'my-projects', label: 'My Projects', description: 'View all projects assigned to you with health and status', route: `projects${pmParam}`, icon: <FolderKanban className="h-5 w-5" /> },
        { id: 'project-status-report', label: 'Project Status Report', description: 'Full status snapshot across all your active projects', route: `reports/project-status${pmParam}`, icon: <ClipboardList className="h-5 w-5" /> },
        { id: 'budget-variance-report', label: 'Budget Variance', description: 'Cost and revenue variance vs budget for your projects', route: `reports/budget-variance${pmParam}`, icon: <TrendingUp className="h-5 w-5" /> },
        { id: 'project-pl', label: 'Project P&L', description: 'Project-level profit and loss with actuals vs budget', route: `reports/project-pl${pmParam}`, icon: <FileText className="h-5 w-5" /> },
        { id: 'wip-report', label: 'WIP Report', description: 'Work in progress balances and aging for your projects', route: `reports/wip${pmParam}`, icon: <BookOpen className="h-5 w-5" /> },
        { id: 'project-forecast', label: 'Project Forecast', description: 'Forward-looking cost and revenue forecast per project', route: `reports/project-forecast${pmParam}`, icon: <BarChart3 className="h-5 w-5" /> },
      ];

      return (
        <Card key="quick_links_pm" className="col-span-full">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick Links</CardTitle>
            <CardDescription>Shortcuts to your most-used reports and views</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 items-stretch">
              {pmQuickLinks.map(({ id, label, description, route, icon }) => (
                <button key={id} onClick={() => handleNavigate(route)} className="group relative flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 text-left transition-colors cursor-pointer hover:border-primary/50 hover:bg-accent/50 h-full justify-between">
                  <div className="flex flex-col items-start gap-2 w-full">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary/20">{icon}</span>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium leading-tight text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{description}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary mt-1" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    default:
      return null;
  }
}
